import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { BottomNavComponent } from './shared/components/bottom-nav/bottom-nav.component';
import { ToastHostComponent } from './shared/components/toast-host/toast-host.component';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, BottomNavComponent, ToastHostComponent],
  template: `
    <div class="min-h-screen bg-[var(--color-app-bg)] text-slate-100">
      <main class="flex min-h-screen w-full flex-col">
        <router-outlet />
      </main>
      @if (showBottomNav()) {
        <app-bottom-nav />
      }
      <app-toast-host />
    </div>
  `,
})
export class App {
  private static readonly lastInspectionRouteKey = 'gf:lastInspectionRoute';
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly document = inject(DOCUMENT);
  private readonly auth = inject(AuthService);
  private readonly currentUrl = signal(this.router.url);

  protected readonly showBottomNav = computed(() => {
    const url = this.currentUrl();
    return !(
      url === '/' ||
      url.startsWith('/login') ||
      url.startsWith('/forgot-password')
    );
  });

  constructor() {
    this.title.setTitle(environment.appName);
    this.auth.loadPermissionsIfAuthenticated();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.persistInspectionRoute(event.urlAfterRedirects);
      });
  }

  private persistInspectionRoute(url: string): void {
    if (!url.startsWith('/car-inspection')) {
      return;
    }

    try {
      sessionStorage.setItem(App.lastInspectionRouteKey, url);
    } catch {
      /* ignore storage errors */
    }
  }

  /** Only restore after a reload on `/` — not during bootstrap when Router.url may still be `/`. */
  private restoreInspectionRouteOnReload(): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }
    const path = win.location.pathname;
    const isLandingOnHomeOnly = path === '/' || path === '';
    if (!isLandingOnHomeOnly) {
      return;
    }

    try {
      const lastRoute = sessionStorage.getItem(App.lastInspectionRouteKey);
      if (!lastRoute || !lastRoute.startsWith('/car-inspection')) {
        return;
      }

      void this.router.navigateByUrl(lastRoute);
    } catch {
      /* ignore storage errors */
    }
  }
}
